import Service, { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { SkipCache } from '@warp-drive/core-types/request';
import type { ObjectValue } from '@warp-drive/core-types/json/raw';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-common/services/session-context';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type { NotificationsService } from 'tio-ui/components/notifications';
import {
  createReportRequest,
  getReportRequest,
  reportFromResource,
  type ReportResource,
} from '../requests/report.ts';
import { buildDuplicateReportPayload } from '../utils/report-duplicate.ts';
import { partnerSpecificFields } from '../utils/partner-specific-fields.ts';

interface DuplicateOptions {
  /** Rechecked against saved attributes, without hydrating or changing the source record. */
  isEligible: (savedReport: Report) => boolean;
  /** Partner company lock; omit for staff copies with no partner audience. */
  lockedCompanyId?: string;
  /** Rechecked before POST so a closed or changed initiating context cannot start a write. */
  canProceed?: () => boolean;
}

/** Serializes creation across report entry points, independently of their component lifetime. */
export default class ReportDuplicationService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;

  /**
   * Fetches the accessible saved source, rechecks eligibility, then creates one private copy.
   * Resolves without a report after a rejected operation or a session change. Never updates the
   * source. Call unlinked() from a component task so closing it cannot release the write guard.
   */
  duplicate = dropTask(async (sourceId: string, options: DuplicateOptions) => {
    const ownerId = this.sessionContext.user?.id;
    if (!ownerId || !sourceId) return;

    let payload: ReturnType<typeof buildDuplicateReportPayload>;
    let created: ReportResource;
    try {
      const request = getReportRequest(sourceId);
      request.cacheOptions = { [SkipCache]: true };
      const { content } = (await this.store.request(request)) as {
        content: { data: ReportResource };
      };
      if (this.sessionContext.user?.id !== ownerId || options.canProceed?.() === false) return;
      if (!content.data?.id || content.data.id !== sourceId) {
        throw new Error('The saved source is unavailable.');
      }
      const attributes = content.data.attributes ?? {};
      const configKey = ['search-config', 'search_config', 'searchConfig'].find(
        (key) => attributes[key] !== undefined
      );
      let searchConfig = configKey ? attributes[configKey] : undefined;
      if (typeof searchConfig === 'string') searchConfig = JSON.parse(searchConfig);
      if (!searchConfig || typeof searchConfig !== 'object' || Array.isArray(searchConfig)) {
        throw new Error('The saved source has no valid report configuration.');
      }
      const savedSource = {
        ...reportFromResource(content.data),
        searchConfig,
      } as unknown as Report;
      if (!options.isEligible(savedSource)) {
        this.notifications.add('This saved report is no longer eligible to duplicate.', {
          appearance: 'error',
        });
        return;
      }
      payload = buildDuplicateReportPayload(savedSource, { companyId: options.lockedCompanyId });
      if (this.sessionContext.user?.id !== ownerId || options.canProceed?.() === false) return;
      const createRequest = createReportRequest(payload);
      createRequest.cacheOptions = { [SkipCache]: true };
      const response = (await this.store.request(createRequest)) as {
        content: { data: ReportResource };
      };
      created = response.content.data;
    } catch (error) {
      console.error('[report-duplication] Could not create copy', error);
      if (this.sessionContext.user?.id === ownerId) {
        this.notifications.add('Could not duplicate this report. Please try again.', {
          appearance: 'error',
        });
      }
      return;
    }
    if (this.sessionContext.user?.id !== ownerId) return;

    this.notifications.add(this.#successMessage(payload.name, payload.searchConfig), {
      appearance: 'success',
    });
    try {
      if (!created?.id) throw new Error('The created copy is missing its identifier.');
      const report = this.store.push<Report>({
        data: {
          id: created.id,
          type: 'report',
          attributes: reportFromResource(created) as ObjectValue,
        },
      });
      return { report }; // Concurrency probes returned objects for hooks that report proxies reject.
    } catch (error) {
      console.error('[report-duplication] Could not hydrate created copy', error);
      this.notifications.add('Your copy was created. Refresh saved reports to open it.', {
        appearance: 'warning',
      });
      return;
    }
  });

  #successMessage(name: string, searchConfig: Record<string, unknown>) {
    const message = `Created "${name}". It starts private and unpublished; schedules are not copied.`;
    const count = partnerSpecificFields(searchConfig).length;
    if (count === 0) return message;
    const fields = count === 1 ? '1 partner-specific field' : `${count} partner-specific fields`;
    return `${message} This report uses ${fields}; the copy will only give complete results for partners that define them.`;
  }
}
